import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { routes } from '../../constants';
import useRouter from '../../utils/useRouter';
import PersonalDetailsAccountSection from './PersonalDetailsAccountSection';
import ProfilePage from '../ProfilePage';
import MasterLayout from '../../components/AppLayout/MasterLayout';
import AssistantSectionTopBar from '../../components/PageToolbar/AssistantSectionTopBar';
import { Box } from '@mui/material';
import useScreenSize from '../../utils/useScreenSize';
import { palette } from '../../assets/styles/themes/tokens';
import { selectUser } from '../../reducers/user.selectors';
import { renderToolbarIcon } from '../../utils/renderToolbarIcon';

export enum PersonalDetailsSection {
  PROFILE = 'profil',
  ACCOUNT = 'konto',
}

const PersonalDetailsPage: React.FC = () => {
  const { router, match } = useRouter();
  const { t } = useTranslation();
  const { isXsScreen, isSmScreen, isMdScreen, isLgScreen } = useScreenSize();
  const isSmallScreen = isXsScreen || isSmScreen;
  const isMdUpScreen = isMdScreen || isLgScreen;
  const user = useSelector(selectUser);
  const isAiOnlyPlan = user?.isAiOnlyUser;
  const [currentSection, setCurrentSection] = useState(
    PersonalDetailsSection.PROFILE
  );
  const isValidParam =
    (match.params.section &&
      match.params.section === PersonalDetailsSection.PROFILE) ||
    match.params.section === PersonalDetailsSection.ACCOUNT;

  useEffect(() => {
    if (isValidParam) {
      setCurrentSection(match.params.section as PersonalDetailsSection);
    } else {
      router.replace(
        `/${routes.personalDetails.root}/${PersonalDetailsSection.PROFILE}`
      );
    }
  }, [match.params.section]);

  const isAccountSection = currentSection === PersonalDetailsSection.ACCOUNT;
  const sectionTitle = isAccountSection ? t('My Account') : t('My Profile');
  const sectionIcon = isAccountSection ? 'cards' : 'user';
  const topBarIcon = renderToolbarIcon(sectionIcon, palette.text.primary, true, '0 0 24 24');

  return (
    <MasterLayout
      customTopBarContent={isAiOnlyPlan && !isSmallScreen ? (
        <AssistantSectionTopBar
          rowSx={{ height: 24 }}
          icon={topBarIcon}
          title={sectionTitle}
          data-testid='personal-details-top-bar'
        />
      ) : undefined}
    >
      {isAiOnlyPlan && isSmallScreen && (
        <AssistantSectionTopBar
          icon={topBarIcon}
          title={sectionTitle}
          data-testid='personal-details-top-bar'
        />
      )}
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: isMdUpScreen ? 'row' : 'column',
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            backgroundColor: palette.background.paperElevation1,
          }}
        >
          {currentSection === PersonalDetailsSection.PROFILE && <ProfilePage />}
          {currentSection === PersonalDetailsSection.ACCOUNT && (
            <PersonalDetailsAccountSection />
          )}
        </Box>
      </Box>
    </MasterLayout>
  );
};

export default PersonalDetailsPage;
